import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowRight, Building2, FileText, Users, PhoneCall, Calendar } from 'lucide-react';
import axios from 'axios';
import Carousel from '../components/Carousel';
import { quickAccessCards } from '../mockData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconComponents = {
  Building2,
  FileText,
  Users,
  Phone: PhoneCall,
  Calendar
};

const Home = () => {
  const [latestNews, setLatestNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);

  useEffect(() => {
    fetchLatestNews();
  }, []);

  const fetchLatestNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news`);
      // Get the 3 most recent news articles
      const sortedNews = response.data
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 3);
      setLatestNews(sortedNews);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoadingNews(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6 animate-fade-in">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Building a Better Future for Hiri Koiari
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                The Hiri Koiari District Development Authority is committed to improving
                infrastructure, health, education, and economic development across our
                district through transparent governance and community-focused initiatives.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/about"
                  className="px-8 py-3 bg-[#F5B700] hover:bg-[#E0A800] text-gray-900 font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  Learn More
                </Link>
                <Link
                  to="/contact"
                  className="px-8 py-3 bg-white border-2 border-[#F5B700] text-gray-900 font-semibold rounded-lg hover:bg-[#F5B700] transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  Get in Touch
                </Link>
              </div>
            </div>

            {/* Right Content - Carousel */}
            <div className="h-[400px] lg:h-[500px] animate-slide-in-right">
              <Carousel />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Bar */}
      <section className="bg-[#2E4A8B] py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-4 text-white">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Location</h3>
                <p className="text-sm text-gray-200">Mirigeda, Central Province, PNG</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 text-white">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Phone</h3>
                <p className="text-sm text-gray-200">82145459</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 text-white">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">Email</h3>
                <p className="text-sm text-gray-200">info.hk@hirikoiari.central.gov.pg</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Access Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-12">
            Quick Access
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickAccessCards.map((card, index) => {
              const IconComponent = iconComponents[card.icon];
              return (
                <Link
                  key={card.id}
                  to={card.link}
                  className="group bg-gray-50 rounded-2xl p-8 hover:bg-white hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-200 transition-colors">
                    <IconComponent className="w-8 h-8 text-[#3B5FA9]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                  <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700">
                    <span>Learn more</span>
                    <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest News Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Latest News & Updates
            </h2>
            <Link
              to="/news"
              className="flex items-center text-blue-600 hover:text-blue-700 font-medium group"
            >
              View all news
              <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {loadingNews ? (
              // Loading skeleton
              [...Array(3)].map((_, index) => (
                <div key={index} className="bg-white rounded-xl p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
                  <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-20"></div>
                </div>
              ))
            ) : latestNews.length === 0 ? (
              <div className="col-span-3 text-center py-8 text-gray-500">
                No news articles yet. Check back soon!
              </div>
            ) : (
              latestNews.map((article, index) => (
                <div
                  key={article.id}
                  className="bg-white rounded-xl p-6 hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <p className="text-blue-600 font-semibold text-sm mb-3">{article.date}</p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{article.description}</p>
                  <Link
                    to={`/news/${article.id}`}
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium group"
                  >
                    Read more
                    <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;